import React, { useEffect, useMemo, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import Select from "react-select";
import toast from "react-hot-toast";
import dayjs from "dayjs";
import { useBranchFilter } from "../../branchFilter/useBranchFilter";
import {
  useCreateFuelRefillMutation,
  useGetFuelCardsQuery,
  useGetFuelCardVehiclesQuery,
  useGetFuelVehiclesQuery,
  useUploadFuelRefillAttachmentMutation,
} from "../fuelApi";
import { FUEL_ENTRY_COLORS, money } from "../fuelFormat";
import { vehicleTypeLabel } from "../../masters/vehicles/vehicleTypes";

// The card picker is a type-to-search (2026-09-23, "add option to search with card number
// and vehicle reg number to pick a card"): the office knows the VEHICLE that needs money
// more often than the card number, so every option carries the registrations the card is
// assigned to and the search matches either.
const selectStyles = {
  control: (base) => ({ ...base, minHeight: 40 }),
  menu: (base) => ({ ...base, zIndex: 20 }),
  menuPortal: (base) => ({ ...base, zIndex: 2000 }),
};
const normalise = (s) => String(s || "").toLowerCase().replace(/\s+/g, "");
const filterCardOption = (option, rawInput) => {
  const q = normalise(rawInput);
  if (!q) return true;
  return option.data.searchText.includes(q);
};

const MAX_ATTACHMENT_BYTES = 2 * 1024 * 1024;
const ATTACHMENT_TYPES = ["image/jpeg", "image/png", "application/pdf"];

/**
 * Two payment modes (Batch200, 2026-10-02):
 *   Card - the classic refill below.
 *   Cash - fuel money handed over in cash for ONE vehicle (vehicles without a card, or a
 *          carded vehicle paying cash once). No card balance moves and nothing is posted
 *          to Petty Cash - it is recorded here so the Monthly Summary shows it as its own
 *          Cash line. Same approval flow.
 *
 * Request a refill: money the OFFICE loads onto a prepaid fuel card. It is not
 * a fuel purchase - those are recorded by drivers in the mobile app. The
 * request is saved as SUBMITTED; the card's balance only moves when someone
 * with Fuel.ApproveRefill approves it.
 *
 * The supporting document is a second call (the API takes it against an
 * existing refill), so it is uploaded straight after the create succeeds. If
 * only the upload fails the refill still stands and the user is told so,
 * rather than the whole thing being reported as failed.
 */
const FuelRefillCreateModal = ({ show, onClose }) => {
  const { selectedBranchId } = useBranchFilter();
  const { data: cards = [] } = useGetFuelCardsQuery({ branchId: selectedBranchId ?? undefined }, { skip: !show });
  // Every live card<->vehicle link in the caller's scope, joined here by card (the card
  // rows carry no vehicles) - default vehicle first.
  const { data: cardVehicleLinks = [] } = useGetFuelCardVehiclesQuery({}, { skip: !show });
  // Cash mode picks a VEHICLE: active ones in the branch, searchable by reg / brand / type.
  // Also names who each vehicle belongs to on the card options (2026-10-09).
  const { data: vehicles = [] } = useGetFuelVehiclesQuery({ branchId: selectedBranchId ?? undefined }, { skip: !show });
  const ownerById = useMemo(() => new Map(vehicles.map((v) => [v.vehicleId, v.allocatedToEmployeeName])), [vehicles]);
  const cardOptions = useMemo(() => {
    const vehiclesByCard = new Map();
    [...cardVehicleLinks]
      .sort((a, b) => Number(b.isDefault) - Number(a.isDefault) || String(a.regNo).localeCompare(String(b.regNo)))
      .forEach((l) => {
        if (!vehiclesByCard.has(l.fuelCardId)) vehiclesByCard.set(l.fuelCardId, []);
        const owner = ownerById.get(l.vehicleId);
        vehiclesByCard.get(l.fuelCardId).push(owner ? `${l.regNo} (${owner})` : l.regNo);
      });
    return cards.map((c) => {
      const regs = vehiclesByCard.get(c.fuelCardId) || [];
      return {
        value: String(c.fuelCardId),
        card: c,
        regs,
        searchText: normalise([c.cardNumber, c.provider, c.branchCode, ...regs].join(" ")),
      };
    });
  }, [cards, cardVehicleLinks, ownerById]);
  const cardsByVehicle = useMemo(() => {
    const map = new Map();
    cardVehicleLinks.forEach((l) => {
      if (!map.has(l.vehicleId)) map.set(l.vehicleId, []);
      map.get(l.vehicleId).push(l.cardNumber);
    });
    return map;
  }, [cardVehicleLinks]);
  const vehicleOptions = useMemo(
    () =>
      vehicles
        .filter((v) => v.isActive)
        .sort((a, b) => String(a.regNo).localeCompare(String(b.regNo)))
        .map((v) => ({
          value: String(v.vehicleId),
          vehicle: v,
          cards: cardsByVehicle.get(v.vehicleId) || [],
          searchText: normalise([v.regNo, v.brand, v.model, v.vehicleType, vehicleTypeLabel(v.vehicleType), v.allocatedToEmployeeName].join(" ")),
        })),
    [vehicles, cardsByVehicle]
  );
  const [createRefill, { isLoading: isCreating }] = useCreateFuelRefillMutation();
  const [uploadAttachment, { isLoading: isUploading }] = useUploadFuelRefillAttachmentMutation();
  const isSaving = isCreating || isUploading;

  const [paymentMode, setPaymentMode] = useState("CARD");
  const [fuelCardId, setFuelCardId] = useState("");
  const [vehicleId, setVehicleId] = useState("");
  const [amount, setAmount] = useState("");
  const [refillDate, setRefillDate] = useState("");
  const [referenceNumber, setReferenceNumber] = useState("");
  const [remarks, setRemarks] = useState("");
  const [file, setFile] = useState(null);
  // Must come AFTER the fuelCardId state: reading it earlier is a temporal-dead-zone
  // ReferenceError on first render, which blanked the whole Refills page (2026-09-27).
  const selectedOption = cardOptions.find((o) => o.value === String(fuelCardId)) || null;
  const selectedVehicleOption = vehicleOptions.find((o) => o.value === String(vehicleId)) || null;
  const isCash = paymentMode === "CASH";

  useEffect(() => {
    if (!show) return;
    setPaymentMode("CARD");
    setFuelCardId("");
    setVehicleId("");
    setAmount("");
    setRefillDate(dayjs().format("YYYY-MM-DD"));
    setReferenceNumber("");
    setRemarks("");
    setFile(null);
  }, [show]);

  const card = cards.find((c) => String(c.fuelCardId) === String(fuelCardId));
  const amountNumber = Number(amount);
  const canSave = Boolean(isCash ? vehicleId : fuelCardId) && amountNumber > 0 && !isSaving;

  const handleFile = (e) => {
    const picked = e.target.files?.[0] || null;
    if (picked && !ATTACHMENT_TYPES.includes(picked.type)) {
      toast.error("The document must be a JPEG, PNG or PDF");
      e.target.value = "";
      return;
    }
    if (picked && picked.size > MAX_ATTACHMENT_BYTES) {
      toast.error("The document must be 2 MB or smaller");
      e.target.value = "";
      return;
    }
    setFile(picked);
  };

  const handleSave = async () => {
    let created;
    try {
      created = await createRefill({
        paymentMode,
        fuelCardId: isCash ? null : Number(fuelCardId),
        vehicleId: isCash ? Number(vehicleId) : null,
        refillAmount: amountNumber,
        refillDate: refillDate || null,
        referenceNumber: referenceNumber.trim() || null,
        remarks: remarks.trim() || null,
      }).unwrap();
    } catch (error) {
      toast.error(error?.message || "Could not submit the refill");
      return;
    }
    if (file && created?.recordGuid) {
      try {
        await uploadAttachment({ recordGuid: created.recordGuid, file }).unwrap();
      } catch (error) {
        toast.error(`${created.refillNumber || "Refill"} was submitted, but the document did not upload: ${error?.message || "upload failed"}`);
        onClose();
        return;
      }
    }
    toast.success(`${created?.refillNumber || "Refill"} submitted for approval`);
    onClose();
  };

  return (
    <Modal show={show} onHide={isSaving ? undefined : onClose} centered size="lg" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>{isCash ? "New cash for fuel" : "New fuel card refill"}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="btn-group mb-3" role="group" aria-label="Payment mode">
          {[
            ["CARD", "Card refill", FUEL_ENTRY_COLORS.REFILL],
            ["CASH", "Direct cash", FUEL_ENTRY_COLORS.CASH],
          ].map(([mode, label, c]) => (
            <button
              key={mode}
              type="button"
              className="btn btn-sm"
              disabled={isSaving}
              onClick={() => setPaymentMode(mode)}
              style={
                paymentMode === mode
                  ? { background: c.text, color: "#fff", borderColor: c.text, minWidth: 120 }
                  : { background: "#fff", color: c.text, borderColor: c.text, minWidth: 120 }
              }
            >
              {label}
            </button>
          ))}
        </div>
        {isCash ? (
          <div className="alert py-2" style={{ fontSize: 13, background: FUEL_ENTRY_COLORS.CASH.bg, color: FUEL_ENTRY_COLORS.CASH.text }}>
            <b>Cash handed over for one vehicle&apos;s fuel.</b> No card is involved, so no card balance changes, and it is not
            posted to Petty Cash. Once approved it shows as the vehicle&apos;s <b>Cash</b> line in the Fuel Monthly Summary.
          </div>
        ) : (
          <div className="alert alert-secondary py-2" style={{ fontSize: 13 }}>
            A refill is <b>money loaded onto the card by the office</b> - it is not a fuel purchase. The card&apos;s balance
            goes up only once this request is approved.
          </div>
        )}
        <div className="row g-3">
          {isCash ? (
            <div className="col-md-8">
              <label className="form-label">Vehicle <span className="text-danger">*</span></label>
              <Select
                classNamePrefix="react-select"
                styles={selectStyles}
                menuPortalTarget={document.body}
                options={vehicleOptions}
                value={selectedVehicleOption}
                onChange={(o) => setVehicleId(o ? o.value : "")}
                filterOption={filterCardOption}
                isClearable
                isSearchable
                autoFocus
                placeholder="Type a vehicle reg number…"
                noOptionsMessage={({ inputValue }) => (inputValue ? `No vehicle matches "${inputValue}"` : "No active vehicles in this branch")}
                getOptionLabel={(o) => o.vehicle.regNo}
                formatOptionLabel={(o, { context }) =>
                  context === "value" ? (
                    <span>
                      <b>{o.vehicle.regNo}</b> <span className="text-muted">- {o.vehicle.brand} {o.vehicle.model}</span>
                      {o.vehicle.allocatedToEmployeeName && <span className="tx-party"> · {o.vehicle.allocatedToEmployeeName}</span>}
                    </span>
                  ) : (
                    <div className="d-flex justify-content-between align-items-start gap-2">
                      <div>
                        <div>
                          <b>{o.vehicle.regNo}</b>{" "}
                          <span className="text-muted">- {o.vehicle.brand} {o.vehicle.model} ({o.vehicle.branchCode})</span>
                        </div>
                        <div className="small text-muted">
                          {vehicleTypeLabel(o.vehicle.vehicleType) || o.vehicle.vehicleType || ""}
                          {o.vehicle.allocatedToEmployeeName ? ` · ${o.vehicle.allocatedToEmployeeName}` : ""}
                        </div>
                      </div>
                      <div className="small text-nowrap" style={{ color: o.cards.length ? "#6c757d" : FUEL_ENTRY_COLORS.CASH.text }}>
                        {o.cards.length ? `card ${o.cards.join(", ")}` : "no card"}
                      </div>
                    </div>
                  )
                }
              />
              <small className="text-muted">The vehicle the cash is for.</small>
            </div>
          ) : (
          <div className="col-md-8">
            <label className="form-label">Fuel card <span className="text-danger">*</span></label>
            <Select
              classNamePrefix="react-select"
              styles={selectStyles}
              menuPortalTarget={document.body}
              options={cardOptions}
              value={selectedOption}
              onChange={(o) => setFuelCardId(o ? o.value : "")}
              filterOption={filterCardOption}
              isClearable
              isSearchable
              autoFocus
              placeholder="Type a card number or a vehicle reg number…"
              noOptionsMessage={({ inputValue }) => (inputValue ? `No card or vehicle matches "${inputValue}"` : "No fuel cards in this branch")}
              getOptionLabel={(o) => `${o.card.cardNumber} ${o.regs.join(" ")}`}
              formatOptionLabel={(o, { context }) =>
                context === "value" ? (
                  <span>
                    <b>{o.card.cardNumber}</b> - {o.card.provider}
                    {o.regs.length > 0 && <span className="text-muted"> · {o.regs.join(", ")}</span>}
                  </span>
                ) : (
                  <div className="d-flex justify-content-between align-items-start gap-2">
                    <div>
                      <div><b>{o.card.cardNumber}</b> <span className="text-muted">- {o.card.provider} ({o.card.branchCode})</span></div>
                      <div className="small" style={{ color: o.regs.length ? "#6E1423" : undefined }}>
                        {o.regs.length ? o.regs.join(", ") : <span className="text-muted">no vehicle assigned</span>}
                      </div>
                    </div>
                    <div className="small text-muted text-nowrap">balance {money(o.card.currentBalance)}</div>
                  </div>
                )
              }
            />
            <small className="text-muted">Search by the card number or by the registration of a vehicle the card is assigned to.</small>
          </div>
          )}
          <div className="col-md-4">
            <label className="form-label">Amount <span className="text-danger">*</span></label>
            <input type="number" min="0" step="any" className="form-control" value={amount} onChange={(e) => setAmount(e.target.value)} />
          </div>
          {!isCash && card && (
            <div className="col-12">
              <div className="small text-muted">
                Current balance <b>{money(card.currentBalance)}</b>
                {amountNumber > 0 && <> → <b>{money(Number(card.currentBalance || 0) + amountNumber)}</b> once approved</>}
              </div>
            </div>
          )}
          <div className="col-md-4">
            <label className="form-label">Refill date</label>
            <input type="date" className="form-control" value={refillDate} onChange={(e) => setRefillDate(e.target.value)} />
          </div>
          <div className="col-md-8">
            <label className="form-label">Reference no.</label>
            <input
              type="text"
              className="form-control"
              placeholder="bank / M-Pesa / provider reference - optional"
              value={referenceNumber}
              onChange={(e) => setReferenceNumber(e.target.value)}
            />
          </div>
          <div className="col-12">
            <label className="form-label">Remarks</label>
            <textarea className="form-control" rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)} placeholder="optional" />
          </div>
          <div className="col-12">
            <label className="form-label">Supporting document</label>
            <input type="file" className="form-control" accept=".jpg,.jpeg,.png,.pdf" onChange={handleFile} />
            <small className="text-muted">Optional - payment slip or provider receipt. JPEG, PNG or PDF, up to 2 MB.</small>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={!canSave}>
          {isSaving ? "Submitting…" : "Submit for approval"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default FuelRefillCreateModal;
